import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { Copy, Download, ExternalLink, MessageCircle } from 'lucide-react'
import { Button } from './ui/Button'
import { toast } from 'sonner'

interface CatalogSharePanelProps {
  brandName: string
  slug: string
}

function getApiBase() {
  const env = import.meta.env.VITE_API_URL || '/api'
  const base = env.replace(/\/$/, '')
  if (base.startsWith('http')) return base
  return `${window.location.origin}${base.startsWith('/') ? base : `/${base}`}`
}

function getCatalogUrl(slug: string) {
  return `${window.location.origin}/catalog/${slug}`
}

/** URL yang dibaca WhatsApp untuk preview gambar (Open Graph di server) */
function getWhatsAppShareUrl(slug: string) {
  return `${getApiBase()}/share/landing/${slug}`
}

export function CatalogSharePanel({ brandName, slug }: CatalogSharePanelProps) {
  const catalogUrl = getCatalogUrl(slug)
  const whatsAppShareUrl = getWhatsAppShareUrl(slug)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')

  useEffect(() => {
    QRCode.toDataURL(catalogUrl, {
      width: 280,
      margin: 2,
      color: { dark: '#0f2b60', light: '#ffffff' },
    })
      .then(setQrDataUrl)
      .catch(() => toast.error('Gagal membuat QR code'))
  }, [catalogUrl])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(catalogUrl)
      toast.success('Link katalog disalin!')
    } catch {
      toast.error('Gagal menyalin link')
    }
  }

  const shareWhatsApp = () => {
    const message = `Lihat katalog produk *${brandName}*:\n${whatsAppShareUrl}`
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer')
  }

  const downloadQr = () => {
    if (!qrDataUrl) return
    const link = document.createElement('a')
    link.href = qrDataUrl
    link.download = `qr-katalog-${slug}.png`
    link.click()
    toast.success('QR code diunduh')
  }

  return (
    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/50 p-4">
      <p className="text-sm font-semibold text-gray-800">Bagikan Katalog</p>
      <p className="mt-1 text-xs text-gray-500 break-all">{catalogUrl}</p>
      <p className="mt-2 text-xs text-gray-500">
        Tombol WhatsApp memakai link preview khusus agar gambar katalog tampil di chat.
        Setelah diklik, pengunjung diarahkan ke katalog.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copyLink}>
          <Copy className="w-4 h-4 mr-1" />
          Salin Link
        </Button>
        <Button type="button" size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={shareWhatsApp}>
          <MessageCircle className="w-4 h-4 mr-1" />
          WhatsApp
        </Button>
        <a href={catalogUrl} target="_blank" rel="noopener noreferrer">
          <Button type="button" variant="outline" size="sm">
            <ExternalLink className="w-4 h-4 mr-1" />
            Buka
          </Button>
        </a>
      </div>

      {qrDataUrl && (
        <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <img
            src={qrDataUrl}
            alt={`QR katalog ${brandName}`}
            className="rounded-lg border border-white bg-white p-2 shadow-sm"
            width={140}
            height={140}
          />
          <div>
            <p className="text-sm text-gray-700">Scan QR untuk membuka katalog</p>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={downloadQr}>
              <Download className="w-4 h-4 mr-1" />
              Unduh QR Code
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
